import React, { useMemo, useState } from "react";
import {
  Edit,
  SaveButton,
  SimpleForm,
  Toolbar,
  TopToolbar,
  useNotify,
} from "react-admin";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { logError } from "../../../utils/appUtils";
import { deepRedirect, getBackPath } from "../../../utils/utils";
import { EventTypeForm } from "./EventTypeForm";
import { DeleteEventTypeButton } from "./DeleteEventTypeButton";
import { usHolidays } from "../../../utils/constants";
import {
  decodeHolidaysInSchedule,
  encodeScheduleHolidaysForMutation,
  initialDecodedScheduleForEventTypeForm,
} from "../../../utils/holidayRules";

export const transformRecordForForm = (record) => {
  const persistedSchedule =
    record?.schedule && typeof record.schedule === "object"
      ? record.schedule
      : null;

  return {
    ...record,
    schedule: persistedSchedule
      ? initialDecodedScheduleForEventTypeForm(persistedSchedule, usHolidays)
      : decodeHolidaysInSchedule(null, usHolidays),
  };
};

const EditToolbar = ({ record, ...props }) => {
  const notify = useNotify();
  const { backPath, history } = props;
  const [saving, setSaving] = useState(false);

  const transform = (data) => {
    data.assetTypeId = data.assetTypeId ? data.assetTypeId : "";
    delete data.assetType;
    encodeScheduleHolidaysForMutation(data);

    // reloadYardCommodity is a @connection object the read query hydrates; only
    // the FK id is valid in UpdateEventTypeInput, so strip the nested object.
    data.reloadYardCommodityId = data.reloadYardCommodityId
      ? data.reloadYardCommodityId
      : "";
    delete data.reloadYardCommodity;

    return data;
  };

  const onSuccess = () => {
    setSaving(false);
    notify(`Event updated successfully.`);
    deepRedirect(history, backPath);
  };

  const onFailure = (error) => {
    setSaving(false);
    logError("eventTypes:EventTypeEdit:onFailure", error, notify);
  };

  return (
    <Toolbar {...props} style={{ justifyContent: "space-between" }}>
      <SaveButton
        label="Save"
        disabled={props.pristine || saving}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <DeleteEventTypeButton
        record={record}
        history={history}
        backPath={backPath}
        notify={notify}
      />
    </Toolbar>
  );
};

const EditActions = (props) => (
  <TopToolbar>
    <BackButton {...props} />
  </TopToolbar>
);

const EventTypeEditForm = ({ record, ...props }) => {
  const backPath = getBackPath(props);
  const initialValues = useMemo(
    () => (record ? transformRecordForForm(record) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- narrow to id/updatedAt, not full record
    [record?.id, record?.updatedAt],
  );
  if (!record) {
    return null;
  }
  return (
    // Forward the Edit-injected controller props (save, saving, version, …):
    // <Edit> clones only its direct child with them, and without `save` the
    // SaveButton throws "onSave.current is not a function".
    <SimpleForm
      {...props}
      record={record}
      variant="outlined"
      submitOnEnter={false}
      initialValues={initialValues}
      toolbar={<EditToolbar {...props} record={record} backPath={backPath} />}
    >
      <EventTypeForm {...props} />
    </SimpleForm>
  );
};

export const EventTypeEdit = (props) => {
  const backPath = getBackPath(props);

  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Event" />}
      mutationMode="pessimistic"
      actions={<EditActions {...props} backPath={backPath} />}
    >
      <EventTypeEditForm {...props} backPath={backPath} />
    </Edit>
  );
};
